import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/compat/router';

import { SmallLoader } from 'shared/components';
import { AnchorButton } from 'src/components/core/button';
import usePaths from 'src/hooks/use-paths';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { useCountdownTimer } from 'src/hooks/use-countdown-timer';
import { LoyaltyPassAddToWallet } from 'src/components/loyalty-pass/loyalty-pass-add-to-wallet';
import { useLoyaltyPass } from 'src/components/loyalty/loyalty-pass/use-loyalty-pass';
import { PinSuccessLayout } from 'src/components/loyalty/modal/components/pin-success-layout';
import { LOYALTY_PASS_DESCRIPTION } from 'src/components/loyalty/loyalty-pass/constants';

type SuccessProps = {
  onClose: () => void;
  programName: string;
};

type View = 'done' | 'fetching' | 'flash' | 'qrCode';

export const Success = ({ onClose, programName }: SuccessProps): JSX.Element => {
  const { shouldShow, loading, loyaltyPassShortUrl } = useLoyaltyPass();

  const flags = useFlags();
  const router = useRouter();
  const isKioskProEnabled = useKioskPro();
  const { href: baseHref } = usePaths();

  const isInstoreEnrollmentEnabled = flags['growth.ecomm.in-store-incentive-enrollment.rollout'] ?? false;
  const isInstoreEnrollment = isInstoreEnrollmentEnabled && router?.pathname?.includes('/instore-enrollment');
  const showAccountLink = !isKioskProEnabled && !isInstoreEnrollment;

  const [view, setView] = useState<View>(() => {
    if (loading) return 'fetching';
    return shouldShow ? 'flash' : 'done';
  });

  // Transition out of 'fetching' once loading resolves
  useEffect(() => {
    if (view !== 'fetching' || loading) return;
    setView(shouldShow ? 'flash' : 'done');
  }, [loading, shouldShow, view]);

  // Start 2s timer when entering 'flash' — only fires when a URL was returned
  const onTimerStop = useCallback(() => setView('qrCode'), []);
  const { startTimer } = useCountdownTimer({ initialSeconds: 2, onTimerStop });
  useEffect(() => {
    if (view === 'flash') {
      startTimer();
    }
  }, [view, startTimer]);

  if (view === 'fetching') {
    return <SmallLoader data-testid='loyalty-success-loader' />;
  }

  if (view === 'flash') {
    return (
      <SuccessContainer>
        <SuccessText>Success!</SuccessText>
      </SuccessContainer>
    );
  }

  if (view === 'qrCode' && loyaltyPassShortUrl) {
    return (
      <LoyaltyPassAddToWallet
        url={loyaltyPassShortUrl}
        onClose={onClose}
        headerText={LOYALTY_PASS_DESCRIPTION}
        closeButtonText='Not right now'
        qrCodeSize={200}
        includeIconRow
      />
    );
  }

  return (
    <PinSuccessLayout programName={programName} onClose={onClose} cancelLabel='Close'>
      <Content>
        <SuccessText>Success!</SuccessText>
      </Content>
      {showAccountLink && (
        <AnchorButton as={Link} href={`${baseHref}/user/loyalty`} width='264px' textAlign='center'>
          View My Account
        </AnchorButton>
      )}
    </PinSuccessLayout>
  );
};

const Content = styled.div`
  color: var(--dcm-modal-secondary-text-color, #646d72);
  margin: 24px 0 48px;
`;

const SuccessText = styled.div`
  color: ${({ theme }) => theme.colors.green[40]};
  font-weight: 600;
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50px;
  margin: 0 auto;
  text-align: center;
`;
